/**
 * @Author: karen Yunuen Pozos Arias
 * @JobTitle : Full Stack Developer
 * @Email : kpozos@ibl.mx
 * @hook        : BuildingMainApp
 * @description : Custom hook for managing the state and logic related to the Building system.
 *                This includes local states for selection, searching, modals, and permissions,
 *                as well as dispatching the Redux action to fetch building data.
 *
 * @dependencies:
 * - Redux (for fetching and accessing building data and permissions)
 * - js-cookie (for getting the selected language)
 * - i18next (for translations)
 *
 * @returns {Object} An object containing state and functions for building management.
 */

import React, {useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import routes from '../../Routes/routes';
import { TABLES } from '../../Apis/Tables';
import TableFunction from "../../components/table/TableFunction";
import DropDownMenuProfile from "../../components/DropDown/DropDownMenuProfile";
import DropDownMenuItem from "../../components/DropDown/DropDownMenuItem";
import { SEARCH_MODEL } from "../../components/table/Model/TableModel";
import {
    DetailsIconsOptions,
    LinkIconsOptions,
    UpdateIconsOptions
} from "../../components/buttons/IconButtons";
import BuildingRenderDetails from "./BuildingRenderDetails";
import CustomChip from "../../components/CustomChip";
import {VIEW_TYPE} from "../../enums/ViewType";
import {statusBorderColor, statusColor, statusFontColor} from "../../enums/statusColorEnum";
import {status} from "../../enums/statusEnum";
import { SimpleSearch } from '../../reduxToolkit/Search/SearchApi';
import { permissionObj } from '../../Helpers/permission';

const BuildingMainApp = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const { permission } = useSelector((state) => state.authenticatioauthennSlice);
    const [createModal, setCreateModal] = useState(false);
    const [updateModal, setUpdateModal] = useState(false);
    const [invitationLinkModal, setInvitationLinkModal] = useState(false);
    const [selected, setSelected] = useState(null);
    const [modified, setModified] = useState(false);
    const [search, setSearch] = useState(false); // Search flag
    const [bodyForExport, setBodyForExport] = useState(''); // Data to export
    const [searchElement, setSearchElement] = useState(''); // Search filter value
    const [simpleSearch, setSimpleSearch] = useState(false); // Simple search flag
    const searchId = 'txt_bm_search_building';
    const option = TABLES.BUILDING;
    const [results, setResults] = useState([]);
    const contractorId = sessionStorage.getItem('contractorId');
    const supplierId = sessionStorage.getItem('providerId');
    const canCreate = DEV || permission?.includes(permissionObj?.WEB_BUILDING_CREATE);
    const canUpdate = DEV || permission?.includes(permissionObj?.WEB_BUILDING_UPDATE);
    // The registration link authorizes a resident to register, it belongs to the residents management
    const canManageResidents = DEV || permission?.includes(permissionObj?.WEB_BUILDING_MANAGE_RESIDENTS);

    const renderOptionsMenu = (item, index) => {
        return <DropDownMenuProfile
            openMenu={openMenu}
            handleMenuToggle={handleMenuToggle}
            menuId={`menu-${item.id}`} // Unique menu ID for each building
        >

            <DropDownMenuItem
                id={"btn_bm_details_"+ item?.id}
                index={index}
                handleClose={handleClose}
                name={t('details')}
                click={() => {
                    navigate(routes.building.details, {
                        state: { id: item.id }
                    });
                }}
                icon={<DetailsIconsOptions/>}
            />

            {canManageResidents &&
                <DropDownMenuItem
                    id={"btn_bm_link_" + item?.id}
                    index={index}
                    handleClose={handleClose}
                    name={t('registration_link')}
                    click={() => {
                        setSelected(item);
                        setInvitationLinkModal(true);
                    }}
                    icon={<LinkIconsOptions/>}
                />
            }

            {canUpdate &&
                <DropDownMenuItem
                    id={"btn_bm_update_"+ item?.id}
                    index={index}
                    handleClose={handleClose}
                    name={t('update')}
                    click={() => {
                        // The update is done on a modal, same form the creation uses
                        setSelected(item);
                        setUpdateModal(true);
                    }}
                    icon={<UpdateIconsOptions/>}
                />
            }
        </DropDownMenuProfile>
    };

    const columns = [
        { field: 'name', label: 'name', fixed: true, left: 0 },
        { field: 'statusId', label: 'status',
            render: (statusId) => <CustomChip
                variant={'large'}
                background={statusColor[statusId]}
                fontColor={statusFontColor[statusId]}
                borderColor={statusBorderColor[statusId]}
                legend={t(status[statusId])}
            />
        },
    ];

    /**
     * Executes a paginated search request using basic criteria.
     *
     * Builds the request body with the current page, page size, selected model,
     * and any additional criteria passed as arguments.
     * Triggers the search action through Redux and updates the results on success.
     */
    const getData = page => {
        const body = SEARCH_MODEL.setObject({page, size:rowsPerPage, model:option, key:searchElement});
        setBodyForExport(body);

        dispatch(SimpleSearch({ body })).then(
            ({ payload: { data: { data, success }}}) => {
                if (success === true) {
                    setResults(data);
                }
            }
        );
    };

    // Use the TableFunction component to handle table logic (pagination, actions menu, etc.)
    const { t, pagination, page, rowsPerPage,
        openMenu, handleMenuToggle, handleClose} = TableFunction({
        data: results, // Table data
        labelRowsPerPage: 'buildings_per_page', // Label for rows per page
        hasContainer: true, // Enable container for the table
        fixedPagination: false, // Disable fixed pagination
        view:VIEW_TYPE.GRID.name,
        searchElement, //New
        getData,//New
        modified,//New
        setModified//New
    });

    /**
     * Rendered by CustomExpandableTable as React.createElement(renderExpandedRow, { row }),
     * so its identity is the component type of the expanded row. It has to stay
     * stable: a new function on every render of the list (opening the options
     * menu, for instance) remounts the detail and repeats its requests.
     */
    const renderExpandedRow = useCallback(({ row }) => {
        return <BuildingRenderDetails row={row}/>;
    }, []);

    return {
        t,
        createModal,
        setCreateModal,
        updateModal,
        setUpdateModal,
        invitationLinkModal,
        setInvitationLinkModal,
        selected,
        setSelected,
        modified,
        setModified,
        DEV,
        permission,
        canCreate,
        canUpdate,
        canManageResidents,
        columns,
        results,
        setResults,
        searchElement,
        setSearchElement,
        search, setSearch,
        option,
        searchId,
        page,
        rowsPerPage,
        simpleSearch,
        setSimpleSearch,
        bodyForExport,
        setBodyForExport,
        contractorId,
        supplierId,
        pagination,
        renderOptionsMenu,
        renderExpandedRow
    };
};

export default BuildingMainApp;
